import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { HeroInboxPreview, type HeroInboxItem, type InboxKind, type VisitorContext } from '@/components/landing/HeroInboxPreview'
import { useGlobeData } from '@/components/globe-visualization/hooks/useGlobeData'
import type { FeedbackSubmission } from '@/components/globe-visualization/types/globe.types'
import { formatCompactTime } from '@/components/brand-mentions/mentionUtils'
import { PLATFORM_LABELS, type BrandMention } from '@/types/brandMentions'
import type { ChatConversation } from '@/types/chat.types'
import { useBrandMentions } from '@/hooks/useBrandMentions'
import { useProgramContext } from '@/context/ProgramContext'
import { useTeamContext } from '@/context/TeamContext'
import { apiClient } from '@/lib/api'

function asRecord(value: unknown): Record<string, unknown> {
	return value && typeof value === 'object' && !Array.isArray(value)
		? (value as Record<string, unknown>)
		: {}
}

function asString(value: unknown): string | undefined {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function asNumber(value: unknown): number | undefined {
	if (typeof value === 'number' && Number.isFinite(value)) return value
	if (typeof value === 'string' && value.trim() !== '') {
		const parsed = Number(value)
		if (Number.isFinite(parsed)) return parsed
	}
	return undefined
}

function deviceType(value: unknown): VisitorContext['device_type'] {
	const normalized = String(value || '').toLowerCase()
	if (normalized === 'desktop' || normalized === 'mobile' || normalized === 'tablet') {
		return normalized
	}
	return undefined
}

function parseScreen(meta: Record<string, unknown>) {
	const width = asNumber(meta.screen_width ?? meta.screenWidth)
	const height = asNumber(meta.screen_height ?? meta.screenHeight)
	if (width && height) return { screen_width: width, screen_height: height }
	const resolution = asString(meta.screen_resolution) || asString(meta.screenResolution)
	if (resolution?.includes('x')) {
		const [w, h] = resolution.split(/x/i)
		return { screen_width: asNumber(w), screen_height: asNumber(h) }
	}
	return {}
}

function visitorContextFromMeta(
	meta: Record<string, unknown>,
	extras: Partial<VisitorContext> = {}
): VisitorContext {
	const parsed = asRecord(meta.parsedUA)
	const browser = asRecord(parsed.browser)
	const os = asRecord(parsed.os)
	const device = asRecord(parsed.device)
	return {
		email: extras.email || asString(meta.email) || asString(meta.userEmail) || asString(meta.user_email),
		browser_name:
			asString(meta.browser_name) ||
			asString(meta.browser) ||
			asString(browser.name),
		browser_version:
			asString(meta.browser_version) ||
			asString(meta.browserVersion) ||
			asString(browser.version),
		os_name: asString(meta.os_name) || asString(meta.os) || asString(os.name),
		os_version: asString(meta.os_version) || asString(meta.osVersion) || asString(os.version),
		device_type: deviceType(meta.device_type || meta.device || device.type),
		...parseScreen(meta),
		current_page_url:
			extras.current_page_url ||
			asString(meta.current_page_url) ||
			asString(meta.page_url) ||
			asString(meta.currentUrl) ||
			asString(meta.pageUrl),
		page_title: extras.page_title || asString(meta.page_title) || asString(meta.pageTitle),
		timezone: asString(meta.timezone),
		language: asString(meta.language),
		city: extras.city || asString(meta.city),
		country_code: extras.country_code || asString(meta.country_code) || asString(meta.countryCode),
		plan: extras.plan || asString(meta.plan),
	}
}

function contextFromSubmission(submission: FeedbackSubmission): VisitorContext {
	const meta = asRecord(submission.userMetadata)
	const custom = asRecord(submission.customFields)
	const session = asRecord(submission.sessionInfo)
	const device = asRecord(submission.deviceInfo)
	return visitorContextFromMeta(
		{
			...custom,
			...session,
			...device,
			...meta,
		},
		{
			email:
				submission.userEmail ||
				asString(meta.email) ||
				asString(custom.email),
			city: submission.location?.city || undefined,
			country_code: submission.location?.countryCode || undefined,
			plan: asString(custom.plan) || asString(meta.plan),
			current_page_url: asString(session.currentUrl) || asString(meta.pageUrl),
			page_title: asString(session.pageTitle) || asString(meta.pageTitle),
		}
	)
}

function reporterDisplayName(submission: FeedbackSubmission, anonymous: string): string {
	const meta = asRecord(submission.userMetadata)
	const custom = asRecord(submission.customFields)
	return (
		asString(submission.userName) ||
		asString(meta.name) ||
		asString(meta.userName) ||
		asString(meta.user_name) ||
		asString(custom.name) ||
		asString(submission.userEmail) ||
		asString(meta.email) ||
		asString(custom.email) ||
		anonymous
	)
}

function mapFeedbackKind(type: string): InboxKind | null {
	switch (type) {
		case 'bug':
			return 'bug'
		case 'feature':
			return 'feature'
		case 'response':
			return 'survey'
		case 'review':
		case 'praise':
			return 'review'
		case 'contact':
			return 'contact'
		case 'chat':
			return 'chat'
		default:
			return null
	}
}

function detailHref(kind: InboxKind, id: string): string | undefined {
	switch (kind) {
		case 'bug':
			return `/bugs?page=1&bugId=${id}`
		case 'feature':
			return `/features?featureId=${id}`
		case 'review':
			return `/reviews?reviewId=${id}`
		case 'survey':
			return `/responses?responseId=${id}`
		case 'contact':
			return `/mailbox?contactId=${id}`
		case 'chat':
			return `/inbox?conversation=${id}`
		default:
			return undefined
	}
}

function itemFromSubmission(
	submission: FeedbackSubmission,
	anonymous: string,
	t: (key: string, options?: Record<string, unknown>) => string
): HeroInboxItem | null {
	const kind = mapFeedbackKind(submission.type)
	if (!kind || kind === 'chat') return null

	const author = reporterDisplayName(submission, anonymous)
	const description = submission.description?.trim() || ''
	const rawTitle = submission.title?.trim() || ''
	const screenshotUrl =
		typeof submission.customFields?.screenshot_url === 'string'
			? submission.customFields.screenshot_url
			: undefined
	const surveyType = rawTitle.toLowerCase()
	const isSurveyTypeLabel = kind === 'survey' && ['nps', 'csat', 'custom', 'response'].includes(surveyType)
	const isGeneratedTitle =
		(kind === 'review' && /^review:/i.test(rawTitle)) ||
		(kind === 'contact' && (rawTitle === author || rawTitle === 'Mailbox Message'))

	const title = isSurveyTypeLabel || isGeneratedTitle ? description || rawTitle : rawTitle || description
	const body = description || rawTitle
	const score = submission.score
	const votes = asNumber(submission.customFields?.upvote_count ?? submission.customFields?.votes)

	let meta = ''
	if (kind === 'review' && score != null) meta = `${score}/5`
	else if (kind === 'survey' && isSurveyTypeLabel && surveyType !== 'custom' && surveyType !== 'response') {
		meta = surveyType.toUpperCase()
	}

	return {
		id: `${kind}:${submission.id}`,
		kind,
		source: 'widget',
		author,
		title,
		body,
		time: formatCompactTime(submission.timestamp, t),
		draft: '',
		meta,
		status: kind === 'bug' || kind === 'feature' || kind === 'contact' ? submission.status : undefined,
		severity: kind === 'bug' || kind === 'contact' ? submission.severity : undefined,
		votes: kind === 'feature' ? votes : undefined,
		hasScreenshot: Boolean(screenshotUrl),
		screenshotUrl,
		href: detailHref(kind, submission.id),
		entityId: submission.id,
		internalNote:
			kind === 'bug' || kind === 'feature' || kind === 'contact'
				? submission.internalNotes || ''
				: undefined,
		context: contextFromSubmission(submission),
		createdAt: submission.timestamp,
	}
}

function itemFromMention(
	mention: BrandMention,
	brandName: string | null,
	t: (key: string, options?: Record<string, unknown>) => string
): HeroInboxItem {
	return {
		id: `mention:${mention.id}`,
		kind: 'mention',
		source: mention.platform,
		author: mention.author || PLATFORM_LABELS[mention.platform],
		title: mention.title || mention.text,
		body: mention.text || mention.title,
		time: formatCompactTime(mention.publishedAt, t),
		draft: '',
		meta: PLATFORM_LABELS[mention.platform],
		href: mention.url,
		hrefExternal: true,
		brandName: brandName || undefined,
		entityId: mention.id,
		createdAt: mention.publishedAt,
		context: {},
	}
}

function itemFromConversation(
	conversation: ChatConversation,
	anonymous: string,
	t: (key: string, options?: Record<string, unknown>) => string
): HeroInboxItem {
	const meta = asRecord(conversation.visitor_metadata)
	const createdAt = conversation.last_message_at || conversation.created_at
	return {
		id: `chat:${conversation.id}`,
		kind: 'chat',
		source: 'widget',
		author:
			conversation.visitor_name ||
			asString(meta.name) ||
			conversation.visitor_email ||
			asString(meta.email) ||
			anonymous,
		title: conversation.last_message_content || t('landing.heroInbox.liveChat'),
		body: conversation.last_message_content || '',
		time: formatCompactTime(createdAt, t),
		draft: '',
		meta: t('landing.heroInbox.liveChat'),
		status: conversation.status,
		href: detailHref('chat', conversation.id),
		entityId: conversation.id,
		createdAt,
		context: visitorContextFromMeta(meta, {
			email: conversation.visitor_email || undefined,
		}),
	}
}

function protectedEndpoint(item: HeroInboxItem) {
	if (!item.entityId) return null
	if (item.kind === 'bug') return `/api/bugs/protected/${item.entityId}`
	if (item.kind === 'feature') return `/api/features/protected/${item.entityId}`
	if (item.kind === 'contact') return `/api/contacts/protected/${item.entityId}`
	return null
}

export function DashboardInbox({ isPro }: { isPro: boolean }) {
	const { t } = useTranslation()
	const { programId, currentProgramAccessLevel } = useProgramContext()
	const { teamId } = useTeamContext()
	const queryClient = useQueryClient()
	const anonymous = t('recentActivityTable.anonymous')
	const canWrite = currentProgramAccessLevel !== 'read'
	const [overrides, setOverrides] = useState<
		Record<string, { status?: string; severity?: string; internalNote?: string }>
	>({})

	const updateMutation = useMutation({
		mutationFn: async ({
			item,
			status,
			severity,
			internalNotes,
		}: {
			item: HeroInboxItem
			status?: string
			severity?: string
			internalNotes?: string
		}) => {
			const endpoint = protectedEndpoint(item)
			if (!endpoint) throw new Error(`Update not supported for ${item.kind}`)
			return apiClient(endpoint, {
				method: 'PUT',
				data: {
					...(status !== undefined ? { status } : {}),
					...(severity !== undefined ? { severity } : {}),
					...(internalNotes !== undefined ? { internal_notes: internalNotes } : {}),
				},
			})
		},
		onMutate: async ({ item, status, severity, internalNotes }) => {
			const previous = overrides[item.id]
			setOverrides((prev) => ({
				...prev,
				[item.id]: {
					...prev[item.id],
					...(status !== undefined ? { status } : {}),
					...(severity !== undefined ? { severity } : {}),
					...(internalNotes !== undefined ? { internalNote: internalNotes } : {}),
				},
			}))
			return { previous, itemId: item.id }
		},
		onError: (_error, _variables, context) => {
			if (!context) return
			setOverrides((prev) => {
				const next = { ...prev }
				if (context.previous) next[context.itemId] = context.previous
				else delete next[context.itemId]
				return next
			})
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['globe-data'] })
			queryClient.invalidateQueries({ queryKey: ['dashboardData'] })
		},
	})

	const { data: globeData, isLoading: globeLoading } = useGlobeData({
		programId,
		teamId,
		filters: {
			timeRange: 'all',
			types: ['bug', 'feature', 'response', 'contact', 'praise', 'review', 'other'],
		},
		enabled: !!programId && !!teamId,
	})

	const { data: mentionsState, isLoading: mentionsLoading } = useBrandMentions(programId, 40)

	const { data: conversations = [], isLoading: chatsLoading } = useQuery({
		queryKey: ['conversations', programId],
		queryFn: async () => {
			const data = await apiClient<ChatConversation[]>(
				`/api/chat/conversations?programId=${programId}`
			)
			return Array.isArray(data) ? data : []
		},
		enabled: !!programId,
		staleTime: 60 * 1000,
	})

	const items = useMemo(() => {
		const feedbackItems = (globeData?.submissions || [])
			.map((submission) => itemFromSubmission(submission, anonymous, t))
			.filter((item): item is HeroInboxItem => item !== null)

		const mentionItems = (mentionsState?.mentions || []).map((mention) =>
			itemFromMention(mention, mentionsState?.brandName || null, t)
		)

		const chatItems = conversations.map((conversation) =>
			itemFromConversation(conversation, anonymous, t)
		)

		return [...feedbackItems, ...mentionItems, ...chatItems]
			.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
			.map((item) => {
				const override = overrides[item.id]
				return override ? { ...item, ...override } : item
			})
	}, [anonymous, conversations, globeData?.submissions, mentionsState, overrides, t])

	return (
		<HeroInboxPreview
			marketingMode
			live
			items={items}
			isLoading={globeLoading || mentionsLoading || chatsLoading}
			isPro={isPro}
			onStatusChange={
				canWrite ? (item, status) => updateMutation.mutate({ item, status }) : undefined
			}
			onSeverityChange={
				canWrite ? (item, severity) => updateMutation.mutate({ item, severity }) : undefined
			}
			onInternalNoteChange={
				canWrite
					? (item, notes) => updateMutation.mutate({ item, internalNotes: notes })
					: undefined
			}
			isUpdating={
				updateMutation.isPending &&
				(updateMutation.variables?.status !== undefined ||
					updateMutation.variables?.severity !== undefined)
			}
			isSavingNote={
				updateMutation.isPending && updateMutation.variables?.internalNotes !== undefined
			}
			canReply={canWrite}
		/>
	)
}
